import { useMemo } from "react";

import type { TimetablePeriod, TimetableSlot } from "../../services/timetableService";
import { DAYS, clock, toMinutes } from "./timetableLogic";

// One colour per subject, stable across the week, so a glance separates one
// recurring lesson from another. Colour means "which subject" here, which is
// why these hues are fixed rather than following the role theme.
const SUBJECT_PALETTE = [
  { bg: "bg-blue-50", border: "border-blue-500", text: "text-blue-900", chip: "text-blue-700" },
  { bg: "bg-emerald-50", border: "border-emerald-500", text: "text-emerald-900", chip: "text-emerald-700" },
  { bg: "bg-amber-50", border: "border-amber-500", text: "text-amber-900", chip: "text-amber-700" },
  { bg: "bg-purple-50", border: "border-purple-500", text: "text-purple-900", chip: "text-purple-700" },
  { bg: "bg-rose-50", border: "border-rose-500", text: "text-rose-900", chip: "text-rose-700" },
  { bg: "bg-cyan-50", border: "border-cyan-500", text: "text-cyan-900", chip: "text-cyan-700" },
  { bg: "bg-indigo-50", border: "border-indigo-500", text: "text-indigo-900", chip: "text-indigo-700" },
  { bg: "bg-orange-50", border: "border-orange-500", text: "text-orange-900", chip: "text-orange-700" },
];
export const subjectColor = (subjectName: string) => {
  let hash = 0;
  for (let i = 0; i < subjectName.length; i++) hash = (hash * 31 + subjectName.charCodeAt(i)) >>> 0;
  return SUBJECT_PALETTE[hash % SUBJECT_PALETTE.length];
};

const PX_PER_MINUTE = 1.3; // tall enough that a 30-40 minute period still fits its label
const formatHour = (minutes: number) => {
  const hour = Math.floor(minutes / 60);
  return `${hour % 12 === 0 ? 12 : hour % 12} ${hour < 12 ? "AM" : "PM"}`;
};

/**
 * A calendar week: a column per school day, each lesson placed by its
 * period's real clock time, breaks shaded across the week.
 */
export function WeekGrid({ slots, periods, onOpen, detail }: {
  slots: TimetableSlot[];
  periods: TimetablePeriod[];
  onOpen: (slot: TimetableSlot) => void;
  /** A second line under the subject, e.g. the class for a teacher's week. */
  detail?: (slot: TimetableSlot) => string;
}) {
  const periodById = useMemo(() => new Map(periods.map((period) => [period.id, period])), [periods]);
  const gridDays = useMemo(() => {
    const used = new Set(slots.map((slot) => slot.day_of_week));
    return DAYS.map((_, index) => index).filter((index) => index < 5 || used.has(index));
  }, [slots]);
  const { start, end, marks } = useMemo(() => {
    const times = periods.flatMap((period) => [toMinutes(period.start_time), toMinutes(period.end_time)]);
    const first = times.length ? Math.min(...times) : 8 * 60;
    const last = times.length ? Math.max(...times) : 16 * 60;
    const hours: number[] = [];
    for (let minute = Math.floor(first / 60) * 60; minute <= last; minute += 60) hours.push(minute);
    return { start: first, end: last, marks: hours };
  }, [periods]);
  const height = Math.max(1, end - start) * PX_PER_MINUTE;
  const breaks = periods.filter((period) => period.is_break);

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[720px]" style={{ display: "grid", gridTemplateColumns: `56px repeat(${gridDays.length}, 1fr)` }}>
        <div />
        {gridDays.map((day) => (
          <div key={day} className="border-b border-gray-200 px-2 pb-2 text-center text-sm font-semibold text-gray-700">{DAYS[day]}</div>
        ))}
        <div className="relative" style={{ height }}>
          {marks.map((minute) => (
            <div key={minute} className="absolute right-2 -translate-y-2 text-[11px] text-gray-400" style={{ top: (minute - start) * PX_PER_MINUTE }}>
              {formatHour(minute)}
            </div>
          ))}
        </div>
        {gridDays.map((day) => (
          <div key={day} className="relative border-l border-gray-100" style={{ height }}>
            {marks.map((minute) => (
              <div key={minute} className="absolute inset-x-0 border-t border-gray-100" style={{ top: (minute - start) * PX_PER_MINUTE }} />
            ))}
            {breaks.map((period) => (
              <div
                key={period.id}
                className="absolute inset-x-0 flex items-center justify-center bg-gray-50 text-[10px] uppercase tracking-wide text-gray-400"
                style={{ top: (toMinutes(period.start_time) - start) * PX_PER_MINUTE, height: (toMinutes(period.end_time) - toMinutes(period.start_time)) * PX_PER_MINUTE }}
              >
                {period.name}
              </div>
            ))}
            {slots.filter((slot) => slot.day_of_week === day).map((slot) => {
              const period = periodById.get(slot.period);
              if (!period) return null;
              const top = (toMinutes(period.start_time) - start) * PX_PER_MINUTE;
              const blockHeight = Math.max(30, (toMinutes(period.end_time) - toMinutes(period.start_time)) * PX_PER_MINUTE - 3);
              const color = subjectColor(slot.subject_name);
              return (
                <button
                  key={slot.id}
                  type="button"
                  onClick={() => onOpen(slot)}
                  className={`absolute left-1 right-1 overflow-hidden rounded-md border-l-4 px-2 py-1 text-left transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${color.bg} ${color.border}`}
                  style={{ top, height: blockHeight }}
                >
                  <div className={`truncate text-xs font-semibold leading-tight ${color.text}`}>{slot.subject_name}</div>
                  {blockHeight > 34 && (
                    <div className={`truncate text-[10px] leading-tight opacity-80 ${color.chip}`}>
                      {detail ? detail(slot) : `${clock(period.start_time)}–${clock(period.end_time)}`}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
